import { use } from "react";

import { ContentContext } from "lib/content/use-content";
import { useToc } from "lib/toc/use-toc";
import type { Index, Page, RenderingContext, Xpointer } from "lib/types";

import {
  BUFFER_SIZE,
  ContentLayoutContext,
} from "../layout/use-content-layout";
import {
  getXpointersOnPage,
  indexToContentNode,
  indexToPage,
  indexToXpointer,
  pageToBookmark,
  pageToXpointer,
  xpointerToChapter,
  xpointerToContentNode,
  xpointerToIndex,
  xpointerToLocation,
  xpointerToPage,
} from "./paths";

export function usePaths() {
  const { layout } = use(ContentLayoutContext)!;
  const { toc } = useToc();
  const { content, initialPartIndex } = use(ContentContext)!;

  if (!toc || !content) {
    throw new Error(
      "usePaths must be used within ContentProvider and TocProvider"
    );
  }

  const isFullContentLoaded = content.length === toc.Parts.length;
  const partsStartIndex = isFullContentLoaded
    ? 0
    : Number.isFinite(BUFFER_SIZE)
      ? Math.max(0, initialPartIndex - BUFFER_SIZE)
      : 0;

  const context: RenderingContext = {
    parts: content,
    partsStartIndex,
    toc,
    layout,
  };

  return {
    indexToXpointer: (index: Index) => indexToXpointer(index, context),
    indexToContentNode: (index: Index) => indexToContentNode(index, context),
    xpointerToIndex: (xpointer: Xpointer) => xpointerToIndex(xpointer, context),
    xpointerToContentNode: (xpointer: Xpointer) =>
      xpointerToContentNode(xpointer, context),
    xpointerToLocation: (xpointer: Xpointer) =>
      xpointerToLocation(xpointer, context),
    xpointerToPage: (xpointer: Xpointer) => xpointerToPage(xpointer, context),
    pageToXpointer: (page: Page) => pageToXpointer(page, context),
    pageToBookmark: (page: Page) => pageToBookmark(page, context),
    indexToPage: (index: Index) => indexToPage(index, context),
    xpointerToChapter: (xpointer: Xpointer) =>
      xpointerToChapter(xpointer, context),
    getXpointersOnPage: (page: Page) => getXpointersOnPage(page, context),
    context,
  };
}
